import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, qs } from '@/lib/api';
import { dateTime } from '@/lib/utils';
import { DataTable, Dialog, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { apiMessage } from './LabBenchErrors';

interface Reporter {
  id: string;
  name: string;
  designation: string | null;
}

interface Referral {
  id: string;
  studyId: string;
  note: string | null;
  answer: string | null;
  answeredAt: string | null;
  createdAt: string;
  fromName: string | null;
  toName: string | null;
  study: { id: string; number: string; modality: string; status: string; studyType: { name: string }; patient: { fullName: string; mrn: string } } | null;
}

export function SecondOpinionDialog({ studyId, studyLabel, onClose }: { studyId: string; studyLabel: string; onClose: () => void }) {
  const toast = useToast();
  const [toUserId, setToUserId] = useState('');
  const [note, setNote] = useState('');
  const reporters = useQuery({ queryKey: ['radiology-reporters'], queryFn: () => get<Reporter[]>('/radiology/reporters'), staleTime: 10 * 60_000 });
  const ask = useMutation({
    mutationFn: () => post(`/radiology/studies/${studyId}/second-opinion`, { toUserId, note: note.trim() || undefined }),
    onSuccess: () => {
      toast.success(tr('radiology', 'second_opinion_asked_for'));
      onClose();
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('radiology', 'ask_for_a_second_opinion')}
      description={studyLabel}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('radiology', 'cancel')}
          </Button>
          <Button onClick={() => ask.mutate()} loading={ask.isPending} disabled={!toUserId}>
            {tr('radiology', 'ask')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {ask.error && <Alert variant="destructive">{apiMessage(ask.error)}</Alert>}
        <Field label={tr('radiology', 'radiologist')} required>
          <Select value={toUserId} onChange={(e) => setToUserId(e.target.value)}>
            <option value="">{tr('radiology', 'choose_a_colleague')}</option>
            {(reporters.data ?? []).map((reporter) => (
              <option key={reporter.id} value={reporter.id}>
                {reporter.name}
                {reporter.designation ? ` (${reporter.designation})` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('radiology', 'what_to_look_at')} hint={tr('radiology', 'optional')}>
          <Textarea rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

export function SecondOpinionsList({ onOpenStudy }: { onOpenStudy: (studyId: string) => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [box, setBox] = useState<'to' | 'from'>('to');
  const [answering, setAnswering] = useState<Referral | null>(null);
  const [answer, setAnswer] = useState('');
  const list = useInfiniteQuery({
    queryKey: ['radiology-second-opinions', box],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<{ data: Referral[]; nextCursor: string | null }>(`/radiology/second-opinions${qs({ box, limit: 50, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const give = useMutation({
    mutationFn: () => post(`/radiology/second-opinions/${answering?.id}/answer`, { answer: answer.trim() }),
    onSuccess: () => {
      toast.success(tr('radiology', 'second_opinion_given'));
      setAnswering(null);
      setAnswer('');
      void queryClient.invalidateQueries({ queryKey: ['radiology-second-opinions'] });
    },
  });
  const rows = list.data?.pages.flatMap((page) => page.data) ?? [];
  return (
    <>
      <SearchFilterBar
        filters={
          <FilterField label={tr('radiology', 'which_second_opinions')}>
            <Select className="w-56" value={box} onChange={(e) => setBox(e.target.value === 'from' ? 'from' : 'to')} aria-label={tr('radiology', 'which_second_opinions')}>
              <option value="to">{tr('radiology', 'asked_of_me')}</option>
              <option value="from">{tr('radiology', 'asked_by_me')}</option>
            </Select>
          </FilterField>
        }
        onReset={box === 'from' ? () => setBox('to') : undefined}
      />
      <DataTable
        rows={rows}
        loading={list.isLoading} refreshing={list.isFetching}
        error={list.error}
        rowKey={(row) => row.id}
        empty={tr('radiology', 'no_second_opinions')}
        columns={[
          {
            header: tr('radiology', 'study'),
            sort: (row) => row.study?.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.study?.patient.fullName ?? '—'}</p>
                <p className="text-xs text-muted-foreground">
                  {row.study ? `${row.study.studyType.name} · ${row.study.number}` : ''}
                </p>
              </div>
            ),
          },
          {
            header: box === 'to' ? tr('radiology', 'asked_by') : tr('radiology', 'asked_of'),
            sort: (row) => (box === 'to' ? row.fromName : row.toName),
            cell: (row) => (
              <div className="text-xs">
                <p>{box === 'to' ? row.fromName : row.toName}</p>
                <p className="text-muted-foreground">{dateTime(row.createdAt)}</p>
              </div>
            ),
          },
          {
            header: tr('radiology', 'question_and_answer'),
            cell: (row) => (
              <div className="max-w-[22rem] text-xs">
                {row.note && <p>{row.note}</p>}
                {row.answer ? <p className="mt-1 font-medium">{row.answer}</p> : <Badge variant="warning">{tr('radiology', 'waiting')}</Badge>}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                <Button size="sm" variant="ghost" onClick={() => onOpenStudy(row.studyId)}>
                  {tr('radiology', 'open')}
                </Button>
                {box === 'to' && !row.answeredAt && (
                  <Button size="sm" onClick={() => setAnswering(row)}>
                    {tr('radiology', 'answer')}
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
      {answering && (
        <Dialog
          open
          onClose={() => setAnswering(null)}
          title={tr('radiology', 'give_your_second_opinion')}
          description={answering.study ? `${answering.study.patient.fullName} · ${answering.study.studyType.name}` : undefined}
          footer={
            <>
              <Button variant="ghost" onClick={() => setAnswering(null)}>
                {tr('radiology', 'cancel')}
              </Button>
              <Button onClick={() => give.mutate()} loading={give.isPending} disabled={answer.trim().length < 3}>
                {tr('radiology', 'send')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            {give.error && <Alert variant="destructive">{apiMessage(give.error)}</Alert>}
            {answering.note && <p className="text-sm text-muted-foreground">{answering.note}</p>}
            <Field label={tr('radiology', 'your_opinion')} required>
              <Textarea rows={5} maxLength={2000} value={answer} onChange={(e) => setAnswer(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      )}
    </>
  );
}
